import React, { useEffect, useState } from 'react';
import {
  Square,
  Button,
  SvgX,
  Heading,
  Row,
  Column,
  Stack,
  Flex,
  Copy,
  SvgInfo,
  Loader,
  FormInput,
  Label,
  UtilityBanner,
  Squish,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { ROLES } from 'src/types/Employee';
import { Input } from 'src/components/Input/Input';
import { Modal } from 'src/components/Modal/Modal';
import {
  getFormattedPhoneNumber,
  PhoneMaskInput,
  removePhoneNumberFormatting,
} from 'src/components/PhoneMaskInput/PhoneMaskInput';
import { sdk } from 'src/utilities/sdk';
import { ILocation } from 'src/types/Location';
import {
  useLocationSearch,
  IDepartment,
  useDepartmentSearch,
} from 'src/utilities/useSearchHooks';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { SelectSearch } from 'src/components/SelectSearch/SelectSearch';
import { Select } from 'src/components/Select/Select';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { SsoIdentifierTypes } from 'src/types/Corp';
import { EMAIL_REGEX } from 'src/utilities/regexPatterns';
import { employeeFormDefaultValues } from 'src/components/enterprise/features/Employee/components/Form/constants/EmployeeDefaultValues';
import { EmployeeFormFields } from 'src/components/enterprise/features/Employee/components/Form/types/EmployeeForm';

interface IManageEnterpriseEmployeeModalProps {
  employeeId?: string;
  corpId: string;
  closeModal: () => void;
  onSuccess: (id?: string) => void;
}

interface CorpDinerSettings {
  allocations_permitted?: boolean;
  sso_unique_identifier?: string;
  sent_welcome_email?: string | null;
}

export interface IEmployee {
  id?: string;
  owner_id?: string;
  internal_id?: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  corp_tags?: string[];
  role: ROLES;
  department_id?: string;
  location_id?: string;
  sub_department?: string;
  corp_diner_settings?: CorpDinerSettings;
}

const EMPLOYEE_ROLE_LABELS = {
  [ROLES.DINER]: 'Standard User',
  [ROLES.ADMIN]: 'Admin User',
  [ROLES.FINANCIAL_ADMIN]: 'Corporate Financial Admin',
};

export function ManageEnterpriseEmployeeModal({
  employeeId,
  corpId,
  closeModal,
  onSuccess,
}: IManageEnterpriseEmployeeModalProps): JSX.Element {
  const isEditing = !!employeeId;
  const [isLoadingDinerData, setIsLoadingDinerData] = useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<IDepartment>(null);
  const [selectedLocation, setSelectedLocation] = useState<ILocation>(null);
  const {
    handleSubmit,
    register,
    unregister,
    errors,
    formState,
    setValue,
    getValues,
    control,
  } = useForm<EmployeeFormFields>({
    mode: 'all',
    defaultValues: employeeFormDefaultValues,
  });
  const { isSubmitting, isValid } = formState;

  const {
    enterpriseData: { isInternalAdminUser, isOnlyCorpAdminUser },
    corpData,
  } = useAuth();

  const {
    sftpExpenseCodesEnabled,
    sftpEmployeesEnabled,
    locationsRequired,
    departmentsRequired,
    subDepartmentEnabled,
    allocationsPermitted,
    ssoEnabled,
    ssoIdentifier,
    ssoUniqueIdentifier,
  } = corpData || {};

  useEffect(() => {
    if (employeeId) {
      getDinerInfo(employeeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    // register form fields for the SelectSearch components
    register(
      { name: 'departmentId' },
      {
        required: {
          value: departmentsRequired,
          message: 'A department is required',
        },
      }
    );
    register(
      { name: 'locationId' },
      {
        required: {
          value: locationsRequired,
          message: 'A location is required',
        },
      }
    );

    return () => {
      unregister('departmentId');
      unregister('locationId');
    };
  }, [register, unregister, departmentsRequired, locationsRequired]);

  function updateFormValue<
    EmployeeFormFieldName extends keyof EmployeeFormFields
  >(
    fieldName: EmployeeFormFieldName,
    fieldValue: EmployeeFormFields[EmployeeFormFieldName]
  ): void {
    setValue(fieldName, fieldValue, { shouldValidate: true });
  }

  async function getDinerInfo(dinerId: string): Promise<void> {
    try {
      setIsLoadingDinerData(true);
      const {
        first_name,
        last_name,
        email,
        phone_number,
        role,
        internal_id,
        location_id,
        department_id,
        sub_department,
        corp_diner_settings: {
          allocations_permitted,
          sso_unique_identifier,
          sent_welcome_email,
        },
      }: IEmployee = await sdk.http.get(
        `/corp-admin/${corpId}/diners/${dinerId}`
      );

      // if user has a location assigned, get the details in order to populate the dropdown
      if (location_id) {
        const location: ILocation = await sdk.http.get(
          `/corp-admin/${corpId}/locations/${location_id}`
        );

        setSelectedLocation(location);
        updateFormValue('locationId', location_id);
      }

      // if user has a department assigned, get the details in order to populate the dropdown
      if (department_id) {
        const department: IDepartment = await sdk.http.get(
          `/corp-admin/${corpId}/departments/${department_id}`
        );

        setSelectedDepartment(department);
        updateFormValue('departmentId', department_id);
      }

      updateFormValue('firstName', first_name);
      updateFormValue('lastName', last_name);
      updateFormValue('email', email);
      updateFormValue('phoneNumber', getFormattedPhoneNumber(phone_number));
      updateFormValue('role', role);
      updateFormValue('internalId', internal_id);
      updateFormValue('subDepartment', sub_department);
      updateFormValue('corpDinerSettings', {
        allocationsPermitted: allocations_permitted,
        ssoUniqueIdentifier: sso_unique_identifier,
        sentWelcomeEmail: sent_welcome_email,
      });
    } catch (error) {
      pushErrorFromAPI(error);
    } finally {
      setIsLoadingDinerData(false);
    }
  }

  function isDinerAllowedToChangeRole(): boolean {
    if (isEditing) {
      const currentRoleValue = getValues('role');
      return (
        isInternalAdminUser ||
        (isOnlyCorpAdminUser &&
          currentRoleValue !== 'CORP_ADMIN_AND_FINANCIAL_ADMIN')
      );
    }

    return true;
  }

  function getSsoUniqueIdentifier(): string {
    switch (ssoIdentifier) {
      case SsoIdentifierTypes.EMAIL:
        return getValues('email');
      case SsoIdentifierTypes.INTERNAL_ID:
        return getValues('internalId');
      default:
        return getValues('corpDinerSettings.ssoUniqueIdentifier');
    }
  }

  async function onFormSubmit({
    firstName,
    lastName,
    email = getValues('email'),
    phoneNumber,
    role,
    internalId,
    locationId,
    departmentId,
    subDepartment,
    corpDinerSettings: { allocationsPermitted, sentWelcomeEmail },
  }: EmployeeFormFields): Promise<void> {
    const employeeData: IEmployee = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: removePhoneNumberFormatting(phoneNumber),
      role,
      internal_id: internalId,
      location_id: locationId,
      department_id: departmentId,
      sub_department: subDepartment,
      corp_diner_settings: {
        allocations_permitted: allocationsPermitted,
        sso_unique_identifier: getSsoUniqueIdentifier(),
        sent_welcome_email: sentWelcomeEmail,
      },
    };

    // remove any falsy values before adding/updating
    Object.keys(employeeData).forEach(
      (key) => !employeeData[key] && delete employeeData[key]
    );

    const fullEmployeeName = `${firstName} ${lastName}`;
    const actionText = isEditing ? 'updated' : 'added';
    let id;
    try {
      if (isEditing) {
        await updateEmployee(employeeData);
      } else {
        ({ id } = await addEmployee(employeeData));
      }

      pushSuccess(`${fullEmployeeName} was ${actionText}`);
      onSuccess(id);
    } catch (error) {
      pushErrorFromAPI(error, {
        fallbackMessage: `${fullEmployeeName} was not ${actionText}. Try again.`,
      });
    }
  }

  async function addEmployee(employeeInfo: IEmployee) {
    return sdk.http.post(`/corp-admin/${corpId}/diners`, employeeInfo);
  }

  async function updateEmployee(employeeInfo: IEmployee) {
    await sdk.http.put(`/corp-admin/${corpId}/diners/${employeeId}`, {
      ...employeeInfo,
      id: employeeId,
    });
  }

  return (
    <Modal size="sm">
      <Square as="header" dimension="2" width="100%">
        <Button variant="tertiary" type="button" onClick={closeModal}>
          <SvgX size="24px" />
        </Button>
        <Squish inset="md">
          <Stack y="xxx-sm">
            <Heading variant="h1">
              {isEditing ? "Edit this employee's info" : 'Add a new employee'}
            </Heading>
          </Stack>
          {(sftpExpenseCodesEnabled || sftpEmployeesEnabled) && (
            <UtilityBanner
              flavor="error"
              prominence="quiet"
              message="Your company has turned on SFTP, be sure to add the new employee to your CSV after, or they will be deleted."
            />
          )}
        </Squish>
      </Square>
      <Square inset="md" position="relative">
        {isLoadingDinerData && (
          <Flex
            backgroundColor="globals.white75a"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            marginTop="-16px"
            marginLeft="-16px"
            width="100%"
            height="100%"
            zIndex={10}
          >
            <Loader size="md" />
          </Flex>
        )}
        <form>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <input
                type="hidden"
                name="corpDinerSettings.sentWelcomeEmail"
                ref={register}
              />
              <Stack y="sm">
                <Input
                  label="First name *"
                  name="firstName"
                  id="firstName"
                  maxLength={60}
                  ref={register({
                    required: 'First name is required',
                  })}
                  error={errors.firstName}
                />
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Input
                  label="Last name *"
                  name="lastName"
                  id="lastName"
                  maxLength={60}
                  ref={register({
                    required: 'Last name is required',
                  })}
                  error={errors.lastName}
                />
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Input
                  label={
                    isEditing ? (
                      <Flex alignItems="center">
                        <Stack x="xx-sm">
                          <Copy variant="caption" color="secondaryText">
                            Email *
                          </Copy>
                        </Stack>
                        <Tooltip
                          roundedContainer
                          maxWidth="280px"
                          content={
                            <Copy variant="body" color="invertedPrimaryText">
                              To change an email address, deactivate this
                              employee and re-add the employee using the new
                              email address. Remember to add the employee back
                              to the group(s) that they were originally in.
                              Contact your CSM for questions.
                            </Copy>
                          }
                        >
                          <span>
                            <Copy variant="body" color="secondaryText">
                              <SvgInfo />
                            </Copy>
                          </span>
                        </Tooltip>
                      </Flex>
                    ) : (
                      'Email *'
                    )
                  }
                  name="email"
                  id="email"
                  type="email"
                  disabled={isEditing}
                  ref={register({
                    required: 'Email is required',
                    pattern: {
                      value: EMAIL_REGEX,
                      message: 'Email is invalid',
                    },
                  })}
                  error={errors.email}
                />
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <PhoneMaskInput
                  label="Phone"
                  id="phoneNumber"
                  name="phoneNumber"
                  required={false}
                  control={control}
                  error={errors.phoneNumber}
                />
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="sm" data-testid="location-dropdown-container">
                <FormInput inputSize="medium" error={errors.locationId}>
                  <Label htmlFor="locationDropdown">
                    {`Location${locationsRequired ? ' *' : ''}`}
                  </Label>
                  <SelectSearch<ILocation, false>
                    corpId={corpId}
                    maxHeight="200px"
                    displayProp="name"
                    keyName="id"
                    placeholder="Select location"
                    initialSelectedValue={selectedLocation}
                    searchResource={useLocationSearch}
                    onValueChange={(newLocation) => {
                      updateFormValue('locationId', newLocation.id);
                    }}
                  />
                </FormInput>
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="sm" data-testid="department-dropdown-container">
                <FormInput inputSize="medium" error={errors.departmentId}>
                  <Label htmlFor="departmentDropdown">
                    {`Department${departmentsRequired ? ' *' : ''}`}
                  </Label>
                  <SelectSearch<IDepartment, false>
                    corpId={corpId}
                    maxHeight="200px"
                    displayProp="name"
                    keyName="id"
                    placeholder="Select department"
                    isRequiredDepartmentSearch={departmentsRequired}
                    initialSelectedValue={selectedDepartment}
                    searchResource={useDepartmentSearch}
                    onValueChange={(newDepartment: IDepartment): void => {
                      updateFormValue('departmentId', newDepartment.id);
                    }}
                  />
                </FormInput>
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Input
                  label={`Internal ID ${
                    ssoIdentifier === SsoIdentifierTypes.INTERNAL_ID ? '*' : ''
                  }`}
                  name="internalId"
                  id="internalId"
                  ref={register({
                    required: {
                      value: ssoIdentifier === SsoIdentifierTypes.INTERNAL_ID,
                      message: 'Internal ID is required',
                    },
                  })}
                  error={errors.internalId}
                />
              </Stack>
            </Column>
            <Column gutter={12} phone={6}>
              <Stack y="sm">
                <Select
                  label="Role permissions *"
                  name="role"
                  id="role"
                  ref={register({
                    required: 'Role is required',
                  })}
                  disabled={!isDinerAllowedToChangeRole()}
                >
                  <option value={null}> </option>
                  {Object.keys(EMPLOYEE_ROLE_LABELS).map((roleKey) => {
                    // only internal admins can create financial admin users
                    if (
                      !isInternalAdminUser &&
                      roleKey === ROLES.FINANCIAL_ADMIN
                    ) {
                      return null;
                    }

                    return (
                      <option key={roleKey} value={roleKey}>
                        {EMPLOYEE_ROLE_LABELS[roleKey]}
                      </option>
                    );
                  })}
                </Select>
              </Stack>
            </Column>
          </Row>
          <Row gutter={12}>
            {subDepartmentEnabled && (
              <Column gutter={12} phone={6}>
                <Stack y="sm">
                  <Input
                    label="Sub Department *"
                    name="subDepartment"
                    id="subDepartment"
                    ref={register({
                      required: 'Sub Department is required',
                    })}
                    error={errors.subDepartment}
                  />
                </Stack>
              </Column>
            )}
            {ssoEnabled && ssoIdentifier === SsoIdentifierTypes.OTHER && (
              <Column gutter={12} phone={6}>
                <Stack y="sm">
                  <Input
                    label={`${ssoUniqueIdentifier} *`}
                    name="corpDinerSettings.ssoUniqueIdentifier"
                    id="corpDinerSettings.ssoUniqueIdentifier"
                    placeholder="e.g. jg302932"
                    maxLength={30}
                    ref={register({
                      required: `${ssoUniqueIdentifier} is required`,
                    })}
                    error={get(
                      errors,
                      'corpDinerSettings',
                      'ssoUniqueIdentifier'
                    )}
                  />
                </Stack>
              </Column>
            )}
          </Row>
          {allocationsPermitted && (
            <Flex alignItems="center">
              <Checkbox
                id="corpDinerSettings.allocationsPermitted"
                name="corpDinerSettings.allocationsPermitted"
                ariaLabel="Allow allocations"
                cancelLabelBottomMargin
                ref={register}
              />
              <Stack x="xx-sm">
                <Copy variant="body">Allow allocations</Copy>
              </Stack>
              <Tooltip
                roundedContainer
                maxWidth="280px"
                content={
                  <Copy variant="body" color="invertedPrimaryText">
                    Give this employee access to their colleagues’ budgets so
                    they can order for them or together in a single order.
                  </Copy>
                }
              >
                <span>
                  <Copy variant="body" color="secondaryText">
                    <SvgInfo />
                  </Copy>
                </span>
              </Tooltip>
            </Flex>
          )}
        </form>
      </Square>
      <Square as="footer" dimension="2" inset="md">
        <Flex>
          <Stack x="xx-sm">
            <Button
              variant="primary"
              buttonSize="medium"
              disabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleSubmit(onFormSubmit)}
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </Stack>

          <Button
            variant="secondary"
            buttonSize="medium"
            type="button"
            disabled={isSubmitting}
            onClick={closeModal}
          >
            Cancel
          </Button>
        </Flex>
      </Square>
    </Modal>
  );
}
